import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Trans } from "@lingui/macro";

import { BannerNotification } from "widgets/feedback";
import { ErrorBoundary } from "widgets/layouts";
import { useUserStore } from "base/stores";

import { PostHeader } from "./post.header";
import { PostContent } from "./post.content";
import { PostFooter } from "./post.footer";
import { PostSkeleton } from "../skeleton_loader";
import { ERROR_CODE, CHANNEL_CONST } from "@/common/utils/constant";
import { usePostStore } from "../../common/store/post.store";
import { onIntersection } from "@/common/utils/utils";

import styles from "./post.css";

Post.propTypes = {
  teamId: PropTypes.string,
  medium: PropTypes.string,
  mediumId: PropTypes.string,
  roles: PropTypes.array,
  postId: PropTypes.string,
  updatePinnedPost: PropTypes.func,
  pinCount: PropTypes.number,
  isFromSearch: PropTypes.bool,
  hideMoremenu: PropTypes.bool,
  postNotification: PropTypes.object,
  focusedNotification: PropTypes.object,
  showMediumOption: PropTypes.bool,
  postIndex: PropTypes.string,
  shouldCallDefaultComments: PropTypes.bool,
  isFromBookmarks: PropTypes.bool,
  updateBookmarkList: PropTypes.func,
  bookmarkContent: PropTypes.object,
  feedName: PropTypes.string
};

export function Post({
  roles,
  teamId,
  medium,
  mediumId,
  postId,
  updatePinnedPost,
  pinCount,
  isFromSearch,
  hideMoremenu,
  postNotification,
  focusedNotification,
  showMediumOption,
  postIndex,
  shouldCallDefaultComments,
  isFromBookmarks,
  updateBookmarkList,
  bookmarkContent,
  feedName
}) {
  const postContent = usePostStore(
    // eslint-disable-next-line kf/hook-func-name
    useCallback((state) => state.postData[postId], [postId])
  );
  const pinnedPostIds = usePostStore((state) => state.pinnedPostIds);

  const [postData, setPostData] = useState(postContent);
  const [postLoading, setPostLoading] = useState(false);
  const [noPost, setNopost] = useState(false);
  const [notificationId, setNotificationId] = useState(null);
  const postRef = useRef(null);
  let pinnedPostViewed = useRef(false);
  const defaultComments = useRef(false);
  const postSeen = useRef(false);

  useEffect(
    function updatePostContent() {
      if (postContent) {
        setPostData(postContent);
      }
    },
    [postContent]
  );

  useEffect(
    function fetchPostData() {
      if (!postData && !isFromBookmarks) {
        getPostData(postId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postId]
  );

  function getPostData(postId) {
    setPostData(null);
    setPostLoading(true);
    setNopost(false);
    let [getPostDataResponse] = usePostStore
      .getState()
      .getPostByIdData(teamId, mediumId, postId);
    getPostDataResponse.then(
      () => {
        setNopost(false);
        setNotificationId(
          focusedNotification?._id || focusedNotification?.InstanceId || null
        );
        defaultComments.current = true;
        setPostLoading(false);
      },
      (err) => {
        if (
          err.error_code === ERROR_CODE.KISSFLOW_ERROR_23002 ||
          err.error_code === ERROR_CODE.KISSFLOW_ERROR_060103
        ) {
          setNotificationId(
            focusedNotification?._id || focusedNotification?.InstanceId || null
          );
          setNopost(true);
        }
      }
    );
  }

  useEffect(
    function configureNotifcation() {
      if (postId) {
        setNotification();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postId]
  );

  useEffect(
    function setTopicCommentCount() {
      if (isFromBookmarks) {
        setPostData(bookmarkContent);
        defaultComments.current = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFromBookmarks]
  );

  useEffect(
    function checkPostDeleted() {
      if (postData?.isDeleted) {
        setNopost(true);
      } else {
        setNopost(false);
      }
    },
    [postData, postId]
  );

  function setNotification() {
    if (postData) {
      setNopost(false);
      setNotificationId(
        focusedNotification?._id || focusedNotification?.InstanceId || null
      );
    }
  }

  useEffect(
    function checkIntersection() {
      if (
        (medium === CHANNEL_CONST.CHANNEL || CHANNEL_CONST.TEAM) &&
        postData
      ) {
        let timeoutId;
        const { currentUser } = useUserStore.getState();
        let [isCurrentPostViewed] = usePostStore
          .getState()
          .getViewedPost(currentUser._id, postData._id);
        function onIntersect(entries) {
          let entry = entries[0];
          if (!entry.isIntersecting && timeoutId) {
            clearTimeout(timeoutId);
          }
          if (postSeen.current) return;
          postSeen.current = true;
          timeoutId = setTimeout(() => {
            let [isCurrentPostViewed] = usePostStore
              .getState()
              .getViewedPost(currentUser._id, postId);
            if (
              postNotification &&
              postNotification.notificationData?.some(
                (notification) => notification.EntityId === postData._id
              ) &&
              pinnedPostIds.includes(postData._id) &&
              !pinnedPostViewed.current
            ) {
              pinnedPostViewed.current = true;
              return;
            }
            if (!isCurrentPostViewed && entry.isIntersecting) {
              onIntersection(
                teamId,
                mediumId,
                postId,
                currentUser,
                entry.isIntersecting
              );
            }
          }, 3000);
        }
        const intersectionObserver = new IntersectionObserver(onIntersect, {
          threshold: 0.3
        });
        const target = postRef.current;
        if (
          target &&
          !isCurrentPostViewed &&
          currentUser._id !== postData._created_by._id
        ) {
          intersectionObserver.observe(target);
        }
        return () => {
          if (target) {
            intersectionObserver.unobserve(target);
          }
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [postData]
  );

  function updateheaderData(postData, isPin) {
    if (isPin) {
      updatePinnedPost(postData._id, postData.IsPinned);
    } else {
      setPostData(postData);
    }
  }

  if (
    focusedNotification &&
    notificationId !==
      (focusedNotification._id || focusedNotification.InstanceId)
  )
    return null;

  if (postLoading) return <PostSkeleton />;
  if (!postData || !postData._id) return null;

  return !noPost ? (
    <ErrorBoundary>
      <div
        className={
          postNotification
            ? `${styles.notificationCard} ${styles.postWrapper} `
            : `${styles.postWrapper} `
        }
        id={postIndex}
        ref={postRef}
      >
        <React.Fragment>
          <PostHeader
            postData={postData}
            teamId={teamId}
            roles={roles}
            updateheaderData={updateheaderData}
            pinCount={pinCount}
            hideMoreMenu={isFromSearch || hideMoremenu}
            showMediumOption={showMediumOption}
            feedName={feedName}
          />
          <PostContent
            postData={postData}
            teamId={teamId}
            isFromSearch={isFromSearch}
          />
          {!isFromSearch && (
            <PostFooter
              teamId={teamId}
              postData={postData}
              roles={roles}
              postNotification={postNotification}
              focusedNotification={focusedNotification}
              shouldCallDefaultComments={
                defaultComments.current || shouldCallDefaultComments
              }
              updateBookmarkList={updateBookmarkList}
            />
          )}
        </React.Fragment>
      </div>
    </ErrorBoundary>
  ) : (
    noPost && (
      <div className={styles.noPostWrapper}>
        <BannerNotification type={`warning`}>
          <Trans>This post has been deleted</Trans>
        </BannerNotification>
      </div>
    )
  );
}
